import {
  Activity,
  // BarChart3,
  Bot,
  Clock,
  Code,
  Database,
  Gauge,
  GitBranch,
  KeyRound,
  // LayoutGrid,
  Layers,
  LucideIcon,
  Server,
  Settings,
  Shield,
  ShieldCheck,
} from 'lucide-react';

/**
 * Represents a single navigation item in the sidebar
 */
export interface NavItem {
  /** Unique identifier for the nav item */
  id: string;
  /** Display label shown when sidebar is expanded */
  label: string;
  /** Route path for navigation */
  href: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Optional badge count (e.g., for notifications) */
  badge?: number;
  /** Whether to show item (for conditional rendering) */
  enabled?: boolean;
  /** Permission required to see this nav item (hidden if user lacks it) */
  permission?: { resource: string; action: string };
  /** Product feature flag — item hidden when this feature is disabled */
  featureFlag?: string;
  /**
   * Open the route in a new browser tab as a standalone app. The new tab
   * renders without the sidebar (see isStandaloneRoute); the current tab
   * stays where it is.
   */
  openInNewTab?: boolean;
}

/**
 * Represents a group of navigation items
 */
export interface NavGroup {
  /** Unique identifier for the group */
  id: string;
  /** Group label shown as header (optional - hidden when collapsed) */
  label?: string;
  /** Navigation items within this group */
  items: NavItem[];
}

/**
 * Complete navigation configuration
 */
export interface NavigationConfig {
  /** Groups of navigation items */
  groups: NavGroup[];
}

/**
 * Main navigation configuration
 * Add new items here to extend the sidebar navigation
 */
export const navigationConfig: NavigationConfig = {
  groups: [
    {
      id: 'primary',
      // No label for primary navigation
      items: [
        // {
        //   id: 'metadata-services',
        //   label: 'Metadata',
        //   href: '/metadata-services',
        //   icon: LayoutGrid,
        //   permission: { resource: 'metadataservices', action: 'list' },
        // },
        {
          id: 'query-editor',
          label: 'SQL Editor',
          href: '/query-editor',
          icon: Code,
          enabled: true,
          // Hidden for users without SQL execution (e.g. viewers). The page
          // itself carries a matching deep-link gate — it mounts via
          // KeepAliveOutlet, outside ProtectedRoute's reach.
          permission: { resource: 'queryeditor', action: 'execute' },
        },
      ],
    },
    {
      id: 'workflows',
      label: 'Workflows',
      items: [
        {
          id: 'laminar-pipelines',
          label: 'Pipelines',
          href: '/pipelines',
          icon: GitBranch,
          enabled: true,
          featureFlag: 'laminar',
        },
        {
          id: 'metriq',
          label: 'Metriq',
          href: '/metriq',
          icon: Gauge,
          enabled: true,
          featureFlag: 'metriq',
          openInNewTab: true,
        },
      ],
    },
    {
      id: 'data',
      label: 'Data',
      items: [
        {
          id: 'catalogs',
          label: 'Catalogs',
          href: '/catalogs',
          icon: Database,
          permission: { resource: 'catalogs', action: 'list' },
        },
      ],
    },
    {
      id: 'compute',
      label: 'Compute',
      items: [
        {
          id: 'queryservices',
          label: 'Clusters',
          href: '/queryservices',
          icon: Server,
          permission: { resource: 'queryservices', action: 'list' },
        },
        {
          id: 'warmpools',
          label: 'Warm Pools',
          href: '/warmpools',
          icon: Layers,
          // Resource name must match compute-api's ValidResources —
          // 'warmpools' (the route slug) is not a permission resource, and a
          // wrong name here hides the nav item from everyone but admins.
          permission: { resource: 'warmnodepools', action: 'list' },
        },
      ],
    },
    {
      id: 'observe',
      label: 'Observe',
      items: [
        // Gated on the `observe` feature flag (ObservabilityStack CR
        // spec.enabled, surfaced via GET /api/v1/features). The link is shown
        // only when the feature is enabled; the matching /observability route
        // in routes.tsx is wrapped in the same FeatureGatedRoute guard.
        {
          id: 'observability',
          label: 'Observability',
          href: '/observability',
          icon: Activity,
          featureFlag: 'observe',
          enabled: true,
        },
        {
          id: 'query-history',
          label: 'Run History',
          href: '/query-history',
          icon: Clock,
          enabled: true,
        },
        // {
        //   id: 'monitoring',
        //   label: 'Monitoring',
        //   href: '/monitoring',
        //   icon: Activity,
        //   enabled: true,
        // },
      ],
    },
    // {
    //   id: 'dashboards',
    //   label: 'Dashboards',
    //   items: [
    //     {
    //       id: 'laminar-dashboard',
    //       label: 'Dashboards',
    //       href: '/dashboard',
    //       icon: BarChart3,
    //       enabled: true,
    //     },
    //   ],
    // },
    {
      id: 'admin',
      label: 'Administration',
      items: [
        {
          id: 'access-control',
          label: 'Access Control',
          href: '/access-control',
          icon: Shield,
          enabled: true,
          permission: { resource: 'roles', action: 'list' },
        },
        {
          id: 'service-accounts',
          label: 'Service Accounts',
          href: '/service-accounts',
          icon: Bot,
          enabled: true,
          permission: { resource: 'serviceaccounts', action: 'list' },
        },
        {
          id: 'machine-clients',
          label: 'Machine Clients',
          href: '/machine-clients',
          icon: KeyRound,
          enabled: true,
          permission: { resource: 'machineclients', action: 'list' },
        },
        {
          id: 'ip-allowlist',
          label: 'IP Allowlist',
          href: '/ip-allowlist',
          icon: ShieldCheck,
          enabled: true,
        },
        {
          id: 'settings',
          label: 'Settings',
          href: '/settings',
          icon: Settings,
          enabled: true,
        },
      ],
    },
  ],
};

/**
 * True when `pathname` belongs to a nav item flagged `openInNewTab`. Those
 * routes render as standalone apps, so the shell hides the sidebar for them.
 */
export function isStandaloneRoute(pathname: string): boolean {
  return navigationConfig.groups.some((group) =>
    group.items.some(
      (item) =>
        item.openInNewTab && (pathname === item.href || pathname.startsWith(`${item.href}/`))
    )
  );
}
